import {
  buildStatusMetricCards,
  type MetricCardOption,
} from '@/features/metrics/components/statusMetricCards';
import type { DashboardRange } from '@/features/dashboard/hooks';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { TagColor } from '@/shared/data/tags';
import type { SuiteExecutionDto, SuiteListItemDto } from '@flowqa/shared';

export const landingSizes = {
  headerWidth: 900,
  panelsWidth: 1200,
  testingChangedWidth: 900,
  howItWorksWidth: 1200,
  carouselSectionWidth: 1200,
  emailWidth: 900,
  registerInterestWidth: 1080,
} as const;

export const landingExampleDateLabel = 'Mar 3, 2026 - Mar 9, 2026';

export const statusOptions: Array<MetricCardOption<'passed' | 'failed' | 'skipped'>> = [
  { value: 'passed', label: 'Passed', tone: 'success' },
  { value: 'failed', label: 'Failed', tone: 'error' },
  { value: 'skipped', label: 'Skipped', tone: 'warning' },
];

export const baseStatusTotals = {
  '7d': { passed: 412, failed: 28, skipped: 12 },
  '14d': { passed: 1240, failed: 86, skipped: 42 },
  '30d': { passed: 2860, failed: 214, skipped: 98 },
  latest: { passed: 132, failed: 9, skipped: 3 },
  all: { passed: 6840, failed: 532, skipped: 248 },
} satisfies Record<DashboardRange, { passed: number; failed: number; skipped: number }>;

export const statusDeltaMap = {
  '7d': { passed: 16, failed: -3, skipped: 1 },
  '14d': { passed: 42, failed: -6, skipped: 3 },
  '30d': { passed: 98, failed: -12, skipped: 6 },
  latest: { passed: 4, failed: -1, skipped: 0 },
  all: { passed: 140, failed: -18, skipped: 8 },
} satisfies Record<DashboardRange, { passed: number; failed: number; skipped: number }>;

export const formatPassRateValue = (totals: {
  passed: number;
  failed: number;
  skipped: number;
}): string => {
  const total = totals.passed + totals.failed + totals.skipped;
  if (total === 0) return '0%';
  return `${((totals.passed / total) * 100).toFixed(2)}%`;
};

type MetricValueCardMap = Record<DashboardRange, MetricValueItem[]>;

export const metricValueCards: MetricValueCardMap = {
  '7d': [
    {
      id: 'passRate',
      label: 'Pass Rate',
      valueLabel: formatPassRateValue(baseStatusTotals['7d']),
      valueTone: 'success',
      deltaLabel: '+1.6%',
      deltaTone: 'positive',
    },
    {
      id: 'frequency',
      label: 'Frequency',
      valueLabel: '1.6 / day',
      valueTone: 'warning',
      deltaLabel: '+0.2 / day',
      deltaTone: 'positive',
    },
    {
      id: 'avgDuration',
      label: 'Avg. Duration',
      valueLabel: '6m 54s',
      valueTone: 'error',
      deltaLabel: '-42s',
      deltaTone: 'positive',
    },
  ],
  '14d': [
    {
      id: 'passRate',
      label: 'Pass Rate',
      valueLabel: formatPassRateValue(baseStatusTotals['14d']),
      valueTone: 'success',
      deltaLabel: '+2.4%',
      deltaTone: 'positive',
    },
    {
      id: 'frequency',
      label: 'Frequency',
      valueLabel: '2 / day',
      valueTone: 'warning',
      deltaLabel: '+0.4 / day',
      deltaTone: 'positive',
    },
    {
      id: 'avgDuration',
      label: 'Avg. Duration',
      valueLabel: '7m 42s',
      valueTone: 'error',
      deltaLabel: '-1m 08s',
      deltaTone: 'positive',
    },
  ],
  '30d': [
    {
      id: 'passRate',
      label: 'Pass Rate',
      valueLabel: formatPassRateValue(baseStatusTotals['30d']),
      valueTone: 'success',
      deltaLabel: '+1.9%',
      deltaTone: 'positive',
    },
    {
      id: 'frequency',
      label: 'Frequency',
      valueLabel: '1.4 / day',
      valueTone: 'warning',
      deltaLabel: '+0.1 / day',
      deltaTone: 'positive',
    },
    {
      id: 'avgDuration',
      label: 'Avg. Duration',
      valueLabel: '8m 12s',
      valueTone: 'error',
      deltaLabel: '-38s',
      deltaTone: 'positive',
    },
  ],
  latest: [
    {
      id: 'passRate',
      label: 'Pass Rate',
      valueLabel: formatPassRateValue(baseStatusTotals.latest),
      valueTone: 'success',
      deltaLabel: '',
      deltaTone: 'neutral',
    },
    {
      id: 'frequency',
      label: 'Frequency',
      valueLabel: '2.4 / day',
      valueTone: 'warning',
      deltaLabel: '',
      deltaTone: 'neutral',
    },
    {
      id: 'avgDuration',
      label: 'Avg. Duration',
      valueLabel: '6m 18s',
      valueTone: 'error',
      deltaLabel: '',
      deltaTone: 'neutral',
    },
  ],
  all: [
    {
      id: 'passRate',
      label: 'Pass Rate',
      valueLabel: formatPassRateValue(baseStatusTotals.all),
      valueTone: 'success',
      deltaLabel: '',
      deltaTone: 'neutral',
    },
    {
      id: 'frequency',
      label: 'Frequency',
      valueLabel: '1.2 / day',
      valueTone: 'warning',
      deltaLabel: '',
      deltaTone: 'neutral',
    },
    {
      id: 'avgDuration',
      label: 'Avg. Duration',
      valueLabel: '8m 34s',
      valueTone: 'error',
      deltaLabel: '',
      deltaTone: 'neutral',
    },
  ],
};

export const statusTrendSeries = [
  { passed: 64, failed: 34, skipped: 22 },
  { passed: 168, failed: 42, skipped: 28 },
  { passed: 98, failed: 48, skipped: 26 },
  { passed: 192, failed: 36, skipped: 20 },
  { passed: 124, failed: 42, skipped: 24 },
  { passed: 226, failed: 30, skipped: 18 },
  { passed: 152, failed: 37, skipped: 21 },
  { passed: 246, failed: 26, skipped: 16 },
  { passed: 168, failed: 32, skipped: 19 },
  { passed: 276, failed: 22, skipped: 13 },
  { passed: 194, failed: 26, skipped: 14 },
  { passed: 300, failed: 18, skipped: 9 },
  { passed: 218, failed: 16, skipped: 8 },
  { passed: 320, failed: 0, skipped: 0 },
];

const landingSuiteTagsBySuiteId = {
  'suite-1': ['playwright', 'regression'],
  'suite-2': ['api', 'regression'],
  'suite-3': ['integration'],
  'suite-4': [],
  'suite-5': ['api', 'regression'],
} as const;

export const suiteExecutionsRows: SuiteExecutionDto[] = [
  {
    id: 'suite-exec-1',
    runId: 'run-orbit-009',
    resultsFileId: 'results-009',
    suiteId: 'suite-1',
    runNumber: 128,
    suiteName: 'Auth Login flow',
    suiteSlug: 'tests-api-auth-login-e2e',
    isLatest: true,
    status: 'passed',
    durationMs: 240,
    tests: 18,
    failures: 0,
    errors: 0,
    skipped: 0,
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    deletedAt: null,
    projectId: 'project-atlas',
    projectName: 'Atlas',
    projectSlug: 'atlas',
    organisationId: 'org-flowqa',
    createdById: 'user-qa-bot',
    tags: [...landingSuiteTagsBySuiteId['suite-1']],
    resultsFile: { id: 'results-009', fileName: 'results-009.xml', buildId: 'build-114' },
  },
  {
    id: 'suite-exec-3',
    runId: 'run-helio-021',
    resultsFileId: 'results-021',
    suiteId: 'suite-2',
    runNumber: 212,
    suiteName: 'Billing Invoice flow',
    suiteSlug: 'tests-billing-invoice-flow-e2e',
    isLatest: true,
    status: 'failed',
    durationMs: 840,
    tests: 14,
    failures: 2,
    errors: 0,
    skipped: 0,
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000),
    deletedAt: null,
    projectId: 'project-atlas',
    projectName: 'Atlas',
    projectSlug: 'atlas',
    organisationId: 'org-flowqa',
    createdById: 'user-qa-bot',
    tags: [...landingSuiteTagsBySuiteId['suite-2']],
    resultsFile: { id: 'results-021', fileName: 'results-021.xml', buildId: 'build-221' },
  },
  {
    id: 'suite-exec-5',
    runId: 'run-ion-004',
    resultsFileId: 'results-004',
    suiteId: 'suite-3',
    runNumber: 64,
    suiteName: 'Search Query indexing',
    suiteSlug: 'tests-search-query-index-e2e',
    isLatest: true,
    status: 'passed',
    durationMs: 430,
    tests: 11,
    failures: 0,
    errors: 0,
    skipped: 0,
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
    deletedAt: null,
    projectId: 'project-atlas',
    projectName: 'Atlas',
    projectSlug: 'atlas',
    organisationId: 'org-flowqa',
    createdById: 'user-qa-bot',
    tags: [...landingSuiteTagsBySuiteId['suite-3']],
    resultsFile: { id: 'results-004', fileName: 'results-004.xml', buildId: 'build-180' },
  },
  {
    id: 'suite-exec-2',
    runId: 'run-orbit-008',
    resultsFileId: 'results-008',
    suiteId: 'suite-1',
    runNumber: 127,
    suiteName: 'Auth Login flow',
    suiteSlug: 'tests-api-auth-login-e2e',
    isLatest: false,
    status: 'failed',
    durationMs: 520,
    tests: 18,
    failures: 2,
    errors: 0,
    skipped: 0,
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000),
    deletedAt: null,
    projectId: 'project-atlas',
    projectName: 'Atlas',
    projectSlug: 'atlas',
    organisationId: 'org-flowqa',
    createdById: 'user-qa-bot',
    tags: [...landingSuiteTagsBySuiteId['suite-1']],
    resultsFile: { id: 'results-008', fileName: 'results-008.xml', buildId: 'build-113' },
  },
  {
    id: 'suite-exec-4',
    runId: 'run-helio-020',
    resultsFileId: 'results-020',
    suiteId: 'suite-2',
    runNumber: 211,
    suiteName: 'Billing Invoice flow',
    suiteSlug: 'tests-billing-invoice-flow-e2e',
    isLatest: false,
    status: 'failed',
    durationMs: 980,
    tests: 14,
    failures: 2,
    errors: 1,
    skipped: 1,
    timestamp: new Date(Date.now() - 7 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 7 * 60 * 60 * 1000),
    deletedAt: null,
    projectId: 'project-atlas',
    projectName: 'Atlas',
    projectSlug: 'atlas',
    organisationId: 'org-flowqa',
    createdById: 'user-qa-bot',
    tags: [...landingSuiteTagsBySuiteId['suite-2']],
    resultsFile: { id: 'results-020', fileName: 'results-020.xml', buildId: 'build-220' },
  },
];

export const suitesTableRows: SuiteListItemDto[] = [
  {
    id: 'suite-1',
    slug: 'api-auth-login',
    name: 'Auth Login flow',
    organisationId: 'org-flowqa',
    projectId: 'project-atlas',
    project: { id: 'project-atlas', slug: 'atlas', name: 'Atlas' },
    latestExecution: {
      id: 'suite-exec-1',
      status: 'passed',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      durationMs: 240,
      runNumber: 128,
    },
    executionStatusCounts: {
      total: 1,
      passed: 1,
      failed: 0,
      errored: 0,
      skipped: 0,
      untested: 0,
    },
    metrics: {
      passRate: 98.4,
      frequencyPerDay: 6.2,
      averageDurationMs: 260,
      flakeScore: 0.12,
      mttfSeconds: 7600,
      delta: {
        passRate: 1.3,
        frequencyPerDay: 1.1,
        averageDurationMs: -40,
        flakeScore: -0.06,
        mttfSeconds: 980,
      },
    },
    latestTestResults: {
      counts: { passed: 17, failed: 0, errored: 0, skipped: 1, untested: 0 },
    },
    tags: [...landingSuiteTagsBySuiteId['suite-1']],
    deletedAt: null,
  },
  {
    id: 'suite-2',
    slug: 'billing-invoice-flow',
    name: 'Billing Invoice flow',
    organisationId: 'org-flowqa',
    projectId: 'project-atlas',
    project: { id: 'project-atlas', slug: 'atlas', name: 'Atlas' },
    latestExecution: {
      id: 'suite-exec-3',
      status: 'failed',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
      durationMs: 840,
      runNumber: 212,
    },
    executionStatusCounts: {
      total: 1,
      passed: 0,
      failed: 1,
      errored: 0,
      skipped: 0,
      untested: 0,
    },
    metrics: {
      passRate: 72.4,
      frequencyPerDay: 4.8,
      averageDurationMs: 790,
      flakeScore: 0.15,
      mttfSeconds: 6400,
      delta: {
        passRate: 1.8,
        frequencyPerDay: -1.3,
        averageDurationMs: -110,
        flakeScore: -0.08,
        mttfSeconds: 1200,
      },
    },
    latestTestResults: {
      counts: { passed: 12, failed: 2, errored: 0, skipped: 0, untested: 0 },
    },
    tags: [...landingSuiteTagsBySuiteId['suite-2']],
    deletedAt: null,
  },
  {
    id: 'suite-3',
    slug: 'search-query-index',
    name: 'Search Query indexing',
    organisationId: 'org-flowqa',
    projectId: 'project-atlas',
    project: { id: 'project-atlas', slug: 'atlas', name: 'Atlas' },
    latestExecution: {
      id: 'suite-exec-5',
      status: 'passed',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      durationMs: 430,
      runNumber: 64,
    },
    executionStatusCounts: {
      total: 1,
      passed: 1,
      failed: 0,
      errored: 0,
      skipped: 0,
      untested: 0,
    },
    metrics: {
      passRate: 89.2,
      frequencyPerDay: 2.1,
      averageDurationMs: 480,
      flakeScore: 0.32,
      mttfSeconds: 3200,
      delta: {
        passRate: -2.1,
        frequencyPerDay: -0.4,
        averageDurationMs: -70,
        flakeScore: 0.08,
        mttfSeconds: -900,
      },
    },
    latestTestResults: {
      counts: { passed: 11, failed: 0, errored: 0, skipped: 0, untested: 0 },
    },
    tags: [...landingSuiteTagsBySuiteId['suite-3']],
    deletedAt: null,
  },
  {
    id: 'suite-4',
    slug: 'notifications-email-dispatch',
    name: 'Notifications Email delivery',
    organisationId: 'org-flowqa',
    projectId: 'project-atlas',
    project: { id: 'project-atlas', slug: 'atlas', name: 'Atlas' },
    latestExecution: {
      id: 'suite-exec-6',
      status: 'passed',
      timestamp: new Date(Date.now() - 11.5 * 60 * 60 * 1000),
      durationMs: 320,
      runNumber: 98,
    },
    executionStatusCounts: {
      total: 1,
      passed: 1,
      failed: 0,
      errored: 0,
      skipped: 0,
      untested: 0,
    },
    metrics: {
      passRate: 95.6,
      frequencyPerDay: 0.6,
      averageDurationMs: 360,
      flakeScore: 0.12,
      mttfSeconds: 6800,
      delta: {
        passRate: -0.4,
        frequencyPerDay: -0.2,
        averageDurationMs: -30,
        flakeScore: 0.03,
        mttfSeconds: -240,
      },
    },
    latestTestResults: {
      counts: { passed: 14, failed: 0, errored: 0, skipped: 1, untested: 0 },
    },
    tags: [...landingSuiteTagsBySuiteId['suite-4']],
    deletedAt: null,
  },
  {
    id: 'suite-5',
    slug: 'webhook-retry-backoff',
    name: 'API Request validation',
    organisationId: 'org-flowqa',
    projectId: 'project-atlas',
    project: { id: 'project-atlas', slug: 'atlas', name: 'Atlas' },
    latestExecution: {
      id: 'suite-exec-7',
      status: 'passed',
      timestamp: new Date(Date.now() - 11 * 60 * 60 * 1000),
      durationMs: 290,
      runNumber: 76,
    },
    executionStatusCounts: {
      total: 1,
      passed: 1,
      failed: 0,
      errored: 0,
      skipped: 0,
      untested: 0,
    },
    metrics: {
      passRate: 90.5,
      frequencyPerDay: 5.1,
      averageDurationMs: 280,
      flakeScore: 0.18,
      mttfSeconds: 5100,
      delta: {
        passRate: 0.4,
        frequencyPerDay: 1.2,
        averageDurationMs: -60,
        flakeScore: 0.02,
        mttfSeconds: -300,
      },
    },
    latestTestResults: {
      counts: { passed: 9, failed: 0, errored: 0, skipped: 1, untested: 0 },
    },
    tags: [...landingSuiteTagsBySuiteId['suite-5']],
    deletedAt: null,
  },
];

const landingMetricSeriesDates = [
  new Date(2026, 2, 3),
  new Date(2026, 2, 4),
  new Date(2026, 2, 5),
  new Date(2026, 2, 6),
  new Date(2026, 2, 7),
  new Date(2026, 2, 8),
  new Date(2026, 2, 9),
];

export const landingSuiteMetricSeriesBySuiteId = new Map<
  string,
  {
    dates: Date[];
    passRate: number[];
    frequencyPerDay: number[];
    averageDurationMs: number[];
  }
>([
  [
    'suite-1',
    {
      dates: landingMetricSeriesDates,
      passRate: [91.6, 94.2, 92.9, 96.1, 94.8, 97.6, 98.4],
      frequencyPerDay: [3.6, 4.8, 4.1, 5.4, 4.9, 6.4, 6.2],
      averageDurationMs: [460, 340, 410, 280, 335, 245, 260],
    },
  ],
  [
    'suite-2',
    {
      dates: landingMetricSeriesDates,
      passRate: [86.4, 81.1, 79.3, 74.8, 76.2, 70.5, 72.4],
      frequencyPerDay: [6.9, 5.2, 5.8, 4.3, 5.1, 3.9, 4.8],
      averageDurationMs: [1040, 880, 960, 810, 900, 760, 790],
    },
  ],
  [
    'suite-3',
    {
      dates: landingMetricSeriesDates,
      passRate: [96.2, 94.1, 93.5, 90.4, 91.6, 87.8, 89.2],
      frequencyPerDay: [4.1, 3.2, 3.6, 2.4, 2.9, 1.8, 2.1],
      averageDurationMs: [560, 640, 520, 590, 470, 505, 480],
    },
  ],
  [
    'suite-4',
    {
      dates: landingMetricSeriesDates,
      passRate: [97.8, 96.4, 97.1, 95.3, 96.0, 94.8, 95.6],
      frequencyPerDay: [2.3, 1.4, 1.8, 0.8, 1.1, 0.5, 0.6],
      averageDurationMs: [390, 450, 365, 410, 340, 372, 360],
    },
  ],
  [
    'suite-5',
    {
      dates: landingMetricSeriesDates,
      passRate: [85.4, 88.2, 86.9, 89.8, 88.7, 91.3, 90.5],
      frequencyPerDay: [2.1, 3.8, 3.2, 4.9, 4.1, 5.5, 5.1],
      averageDurationMs: [410, 300, 360, 270, 325, 255, 280],
    },
  ],
]);

export const landingTagColorByName: Partial<Record<string, TagColor>> = {
  playwright: 'blue',
  e2e: 'purple',
  regression: 'orange',
  api: 'teal',
  integration: 'green',
  smoke: 'brown',
  nightly: 'purple',
  contract: 'red',
  retry: 'orange',
};

export const buildStatusCards = (
  range: DashboardRange,
): ReturnType<typeof buildStatusMetricCards> => {
  const statusCounts = baseStatusTotals[range];
  const showDelta = range !== 'latest' && range !== 'all';
  return buildStatusMetricCards({
    options: statusOptions,
    counts: statusCounts,
    deltas: showDelta ? statusDeltaMap[range] : undefined,
    total: statusCounts.passed + statusCounts.failed + statusCounts.skipped,
    loading: false,
    showDelta,
  });
};
